import { share, takeUntil } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { SESSION_STORAGE } from '../constants/session-storage';
import { Util } from '../util';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { recordLogoutReason } from '../utils/logout-diagnostics';
export interface indexedDbStore {
  id?: any;
  key: string;
  json: any | string;
  userId?: string;
  crypt?: boolean;
}

@Injectable()
export class IndexedDBService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private onSubject = new Subject<{ key: string; value: any }>();
  public storeName: string;

  private readonly changes$ = this.onSubject.asObservable().pipe(
    takeUntil(this.destroy$),
    share()
  );

  public getChanges(): Observable<{ key: string; value: any }> {
    return this.changes$;
  }

  constructor(
    public dbService: NgxIndexedDBService) {
    this.start();
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up storage event listener
    this.stop();

    // Clean up subjects
    this.onSubject.complete();
  }

  private async getItemChooseKey(key: string, name: string, defaultValue?: any): Promise<indexedDbStore> {
    let response: indexedDbStore = (await firstValueFrom(this.dbService.getByIndex(this.storeName, key, name)) as indexedDbStore);

    if (!Util.isNullOrUndefined(response) && response.crypt === true) {
      try {
        const decodedString = Util.decodeBase64(response.json);
        response.json = JSON.parse(decodedString);
      } catch (error) {
        console.error("Error during decoding or parsing: ", error);
      }
    } else {
      if (Util.isNullOrUndefined(response) && !Util.isNullOrUndefined(defaultValue)) {
        const value = Util.isString(defaultValue) ? defaultValue.replace('"', '') : defaultValue;
        response = { key: name, json: value };
      }
    }

    return response;
  }


  public async getItem(name: string, defaultValue?: any): Promise<indexedDbStore> {
    return this.getItemChooseKey('key', name, defaultValue);
  }

  public async getById(id: number, defaultValue?: any): Promise<indexedDbStore> {
    return this.getItemChooseKey('id', `${id}`, defaultValue);
  }

  public async getAll(): Promise<indexedDbStore[]> {
    return (await firstValueFrom(this.dbService.getAll(this.storeName)) as indexedDbStore[]);
  }

  /**
   * Stores JSON data in the database asynchronously.
   * @param key The key to store the data under.
   * @param json The JSON data to store.
   * @returns The ID of the stored data.
   */
  public async storeAsync(key: string, json: any): Promise<number> {
    const jsonString = JSON.stringify(json);
    const encodedJson = btoa(unescape(encodeURIComponent(jsonString)));

    const existingData = await this.getItem(key);

    let dataId;

    if (Util.isNullOrUndefined(existingData)) {
      dataId = await firstValueFrom(this.dbService.add(this.storeName, { key, json: encodedJson, crypt: true }));
    } else {
      dataId = existingData.id;
      await firstValueFrom(this.dbService.update(this.storeName, { id: existingData.id, key, json: encodedJson, crypt: true }));
    }

    // Return the ID of the data
    return dataId;
  }

  public async removeItem(key): Promise<indexedDbStore[]> {
    const response: indexedDbStore = await this.getItem(key);
    return this.removeById(response?.id);
  }

  public async removeById(id: number): Promise<indexedDbStore[]> {
    //const count = await this.dbService.count(this.storeName, id).toPromise()
    if (id)
      return (firstValueFrom(this.dbService.delete(this.storeName, id)) as unknown as indexedDbStore[])
    else
      return null
  }

  public async reset(): Promise<void> {
    await firstValueFrom(this.dbService.clear(this.storeName));
  }

  public start(): void {
    window.addEventListener('storage', this.storageEventListener.bind(this));
  }

  private storageEventListener(event: StorageEvent): void {
    if (event.storageArea === localStorage || event.storageArea === sessionStorage) {
      let _event;
      try {
        _event = JSON.parse(event.newValue);
        if ((event.key === SESSION_STORAGE.TOKEN && event.newValue == null) || event.key == null) {
          // Logout diagnostics (temporary, QA): this tab follows another tab's logout with a reload.
          recordLogoutReason('storage listener: token removed by another tab, reloading', {
            cause: event.key == null ? 'storage cleared' : 'token removed',
            service: this.constructor?.name,
          });
          Notification.requestPermission((result) => {
            if (result === 'granted') {
              navigator.serviceWorker.ready.then((value) => {
                value.showNotification('Session', {
                  body: 'Session Expired!',
                  icon: '/statics/icons/android-chrome-192x192.png',
                  data: 'Session Expired',
                  //vibrate: [200, 100, 200, 100, 200, 100, 200],
                });
              });
            }

            setTimeout(() => window.location.reload(), 1000);
          });
        }
      } catch (e) {
        _event = event.newValue;
      }
      this.onSubject.next({ key: event.key, value: _event });
    }
  }

  public stop(): void {
    window.removeEventListener('storage', this.storageEventListener.bind(this));
    this.onSubject.complete();
  }
}
